import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../config/api';
import { useAuth } from './AuthContext';

const SettingsContext = createContext(null);

const defaults = {
  pharmacy_name: 'Gaza Pharmacy',
  pharmacy_address: 'Gaza City, Main Street',
  pharmacy_phone: '0599123456',
  pharmacy_email: 'info@mypharmacy.com',
  pharmacy_logo: '',
  currency: 'Rs.',
  invoice_prefix: 'INV',
  tax_rate: '0',
  low_stock_threshold: '10',
  expiry_alert_days: '90',
  receipt_footer: 'Thank you for your purchase!',
  receipt_font: 'consolas',
  receipt_font_weight: 'bold',
  enable_email_notifications: '1',
  enable_low_stock_alerts: '1',
  enable_expiry_alerts: '1',
  two_factor_enabled: '0',
  gemini_api_key: '',
  gemini_model: 'gemini-1.5-flash',
  enable_ai_assistant: '1',
};

export function SettingsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState(defaults);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/settings');
      const data = res?.settings || res;
      if (data && typeof data === 'object') {
        setSettings((prev) => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      const res = await api.get('/settings/branding');
      const data = res?.settings || res;
      if (data && typeof data === 'object') {
        setSettings((prev) => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Failed to fetch shop branding:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // A token is written only after login completes; reload the complete
  // settings then so the cashier's UI updates without a page refresh.
  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
    }
  }, [isAuthenticated, fetchSettings]);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const saveSettings = async (newSettings) => {
    const res = await api.put('/settings', newSettings);
    const data = res?.settings || res;
    if (data && typeof data === 'object') {
      setSettings((prev) => ({ ...prev, ...data }));
    }
    return data;
  };

  const value = {
    settings,
    loading,
    updateSetting,
    saveSettings,
    refresh: fetchSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
